import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  CanActivate,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { UserService } from '../common-services/user.services';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationGuard {
  constructor(private router: Router, private userService: UserService) {}

  /**
   * Check if the user is logged in before calling http
   *
   * @param route
   * @param state
   * @returns {boolean}
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    // Check if the user is logged in
    if (this.userService.isLoggedIn()) {
      const userCredentials = this.userService.currentUserCredantials;

      // Pending delegate status takes precedence over all application access
      if (this.userService.isPendingDelegate) {
        this.router.navigate(['/authentication/pending-approval']);
        return false;
      }

      // Isolated check for future delegate rejection scenario
      if (this.userService.isRejectedDelegate) {
        // Reserved for future rejection flow handling
      }

      // Check if the user's ID is expired
      if (userCredentials.isIdExpired) {
        this.router.navigate(['/authentication/id-expired']);
        return false;
      }

      // Check if the user's account is suspended
      if (userCredentials.isSuspended) {
        this.router.navigate(['/authentication/id-suspended']);
        return false;
      }

      // If no conditions are met, allow navigation
      return true;
    }

    // If not logged in, redirect to the login page
    this.router.navigate(['/authentication/login']);
    return false;
  }
}

@Injectable({
  providedIn: 'root',
})
export class LoginGuard implements CanActivate {
  constructor(private router: Router, private userService: UserService) {}

  canActivate(): boolean | UrlTree {
    if (!this.userService.isLoggedIn()) {
      return true;
    }

    if (this.userService.isPendingDelegate) {
      return this.router.parseUrl('/authentication/pending-approval');
    }

    const userCredentials = this.userService.currentUserCredantials;
    if (userCredentials.isIdExpired) {
      return this.router.parseUrl('/authentication/id-expired');
    }

    if (userCredentials.isSuspended) {
      return this.router.parseUrl('/authentication/id-suspended');
    }

    if (
      !this.userService.isReadOnlyDelegate &&
      userCredentials.userKYCStatus !== 'Pending' &&
      userCredentials.userKYCStatus !== 'Approved' &&
      userCredentials.userKYCStatus !== 'Expired'
    ) {
      return this.router.parseUrl('/kyc/kyc-questions');
    }

    return this.router.parseUrl('/opportunities');
  }
}
